<script setup lang="ts">
  import type { SelectHTMLAttributes } from 'vue'
  import { computed, ref, useId } from 'vue'

  const modelValue = defineModel<SelectHTMLAttributes['value']>()

  const props = defineProps<{
    id?: string,
    icon?: string,
    disabled?: boolean,
    label?: string,
    required?: boolean,
    error?: string | string[],
    placeholder?: string,
    loading?: boolean,
    clearable?: boolean,
  }>()

  const focus = ref(false)

  const defaultId = useId()
  const inputId = computed(() => props.id ?? defaultId)
</script>

<template>
  <div>
    <label
      v-if="label"
      :for="inputId"
      class="form-label">
      {{ label }}
      <span
        v-if="required"
        class="text-danger-600">
        *
      </span>
    </label>
    <div
      class="relative flex items-center rounded border"
      :class="{ 'input-ring': focus }">
      <div
        class="flex-center text-gray-600 shrink-0 pl-2"
        :class="{ 'w-6': !!icon, 'w-1': !icon }">
        <fa-icon
          v-if="icon"
          :icon="icon" />
      </div>
      <select
        :id="inputId"
        v-model="modelValue"
        :required="required"
        :disabled="disabled"
        class="w-full pl-2"
        :class="{'pr-6': !clearable, 'pr-16': clearable}"
        @focus="focus = true"
        @blur="focus = false">
        <option
          v-if="placeholder"
          :value="null"
          hidden>
          {{ placeholder }}
        </option>
        <slot></slot>
      </select>

      <div class="absolute flex items-center text-xs text-gray-600 z-10 right-2 gap-1">
        <app-button
          v-if="modelValue && clearable"
          class="flex-center hover:bg-gray-200 rounded-full size-5"
          @click="modelValue = undefined">
          <fa-icon icon="i-far-xmark" />
        </app-button>

        <span class="pointer-events-none">
          <fa-icon
            v-if="loading"
            icon="i-far-circle-notch"
            class="animate-spin" />
          <fa-icon
            v-else
            icon="i-fas-caret-down" />
        </span>
      </div>
    </div>
    <app-error-msg :msg="error" />
  </div>
</template>

<style scoped>
  @reference "@/assets/styles/main.css";

  /* border on the container div, so that the focus ring enclose the icon as well */
  /* then the select height should less than h-9 */
  select {
    @apply block cursor-pointer appearance-none rounded focus-visible:ring-0;
    @apply text-sm text-gray-700;

    height: calc(var(--h-input) - 2px);
  }
</style>
